















































































































































































import { Component, Vue, Prop, PropSync, Watch } from "vue-property-decorator";
// utilities
import { Chart } from "highcharts-vue";
import Highcharts from "highcharts";
import dateHelper from "@/Scripts/utilities/date-helper";
import moment from "moment";
// @components
import StatusCard from "@/components/diagnostics/StatusCard.vue";
//  types
import { PerformanceIndicatorPeriod } from "@/types/PerformanceIndicatorPeriod";
import { IPlotLine } from "@/types/highcharts/plotLine";
import { LogData } from "@/types/logData";
import { VesselEvent } from "@/types/vesselEvent";
import { IStatusCard } from "@/types/StatusCard";

@Component({
  components: {
    StatusCard,
    Highcharts: Chart,
  },
})
export default class PerformanceIndicatorChart extends Vue {
  @Prop() title!: string;
  @Prop() description!: string;
  @Prop({ required: true }) periods!: PerformanceIndicatorPeriod[];
  @Prop({ required: true }) performanceData!: LogData;
  @Prop({ type: [String, Number], default: null }) readonly triggerValue!: string | number | null; // existing
  @Prop({ type: [String, Number], default: null }) readonly triggerValueDisplay!: string | number | null; // new
  @PropSync("eventsList", { required: true }) syncEvents!: VesselEvent[];

  // @Data
  chart!: any;
  chartLoaded = false;
  selectedEvent: VesselEvent | null = null;
  mutatedPeriods: PerformanceIndicatorPeriod[] = JSON.parse(JSON.stringify(this.periods));

  // @Watchers
  @Watch("eventsList")
  onEventsListChanged(): void {
    setTimeout(() => {
      this.chart.update(this.ChartOptions, true);
      this.chart.reflow();
    }, 500);
  }

  @Watch("selectedEvent")
  onSelectedDockingEventChanged(): void {
    //  re-setup evaluation period and recalculate statistics when event changed
    this.setDryDockingEvaluationPeriod();
  }

  // @Getters
  get cardContentWithCalculating() {
    return (value: string) => {
      const raw = value ?? "";
      const trimmed = String(raw).trim();
      // preserve explicit "Not Available" (don't append "Calculating")
      if (trimmed === "" || trimmed.toLowerCase() === "not available") {
        return trimmed || "Not Available";
      }
      if (this.title === "InService Performance" && !this.hasCalculatedPeriods) {
        // append calculating only for actual numeric/text values
        return `${trimmed} Calculating`;
      }
      return trimmed;
    };
  }

  get performanceChangeCard(): IStatusCard {
    return {
      content: this.stats.differenceInPerformance,
      contentPrependIcon: this.performanceChange.icon,
      contentPrependIconColor: this.performanceChange.color,
      contentTooltip: this.performanceChange.isCalculating
        ? "Waiting for enough valid and stable data within the current period. Performance will be shown once conditions defined by ISO 19030 are met"
        : "",
      footer: "Performance Change",
      footerTooltip: "Difference between the evaluation and reference periods. A positive value indicates improved efficiency after maintenance",
    };
  }

  get maintenanceTriggerCard(): IStatusCard {
    // prefer the actual latest value entered by user for display
    const displayRaw =
      this.triggerValueDisplay !== null && this.triggerValueDisplay !== undefined
        ? String(this.triggerValueDisplay)
        : this.triggerValue !== null && this.triggerValue !== undefined
        ? String(this.triggerValue)
        : null;

    const content = displayRaw === null || displayRaw === "" ? "Not Set" : `${displayRaw}%`;

    return {
      content,
      contentPrependIcon: "$Flash",
      contentPrependIconColor: "BackgroundInfoPrimary",
      contentTooltip: "Threshold defined for the latest maintenance event",
      footer: "Maintenance Trigger",
      footerTooltip: "Speed loss threshold that, when exceeded, indicates maintenance may be needed to restore vessels's performance",
    };
  }

  get referenceCard(): IStatusCard {
    // footer label adjustments per chart type
    const footer =
      this.title === "Maintenance Trigger"
        ? "Reference period"
        : this.title === "Maintenance Effect"
        ? "Before Maintenance (ref)"
        : this.title === "InService Performance"
        ? "After Drydocking (ref)"
        : "Before Drydocking (ref)";

    const rawContent = this.stats.referenceAverage;
    const content = this.cardContentWithCalculating(rawContent);

    // Do not show footer tooltip for Maintenance Effect
    const footerTooltip =
      this.title === "Maintenance Trigger"
        ? ""
        : this.title === "Maintenance Effect"
        ? ""
        : this.title === "InService Performance"
        ? "Used as the baseline to compare future performance during vessel's operation (service)"
        : this.title === "DryDocking Performance"
        ? "Average performance level before most recent drydocking, used as a baseline to assess maintenance impact"
        : "Reference period average";

    return {
      content,
      contentTooltip: this.stats.referenceAverageTooltip,
      contentPrependIcon: "$RCircle",
      contentPrependIconColor: "BackgroundBrandPrimaryHover",
      footer,
      footerTooltip,
    };
  }

  get evaluationCard(): IStatusCard | null {
    if (this.title === "Maintenance Trigger") return null;

    const footer = this.title === "Maintenance Effect" ? "After Maintenance (eval)" : this.title === "InService Performance" ? "In-Service (eval)" : "After Drydocking (eval)";

    const rawContent = this.stats.evaluationAverage;
    const content = this.cardContentWithCalculating(rawContent);

    // Do not show footer tooltip for Maintenance Effect
    const footerTooltip =
      this.title === "Maintenance Effect"
        ? ""
        : this.title === "InService Performance"
        ? "Vessel's performance during its current operation (service), compared to the reference period"
        : this.title === "DryDocking Performance"
        ? "Average performance level after most recent drydocking, reflecting the current hull and propeller condition"
        : "Evaluation period average";

    return {
      content,
      contentPrependIcon: "$ECircle",
      contentPrependIconColor: "BackgroundInfoPrimary",
      footer,
      footerTooltip,
    };
  }

  get statusCardList(): IStatusCard[] {
    const cards: IStatusCard[] = [];

    cards.push(this.performanceChangeCard);

    cards.push(this.referenceCard);
    const evalCard = this.evaluationCard;
    if (evalCard) cards.push(evalCard);

    if (this.title === "Maintenance Trigger") {
      cards.push(this.maintenanceTriggerCard);
    }

    return cards;
  }

  get getTriggerValue(): string {
    return this.triggerValue !== null && this.triggerValue !== undefined ? String(this.triggerValue) : "Not Available";
  }

  get stats(): any {
    switch (this.title) {
      case "DryDocking Performance":
        return {
          evaluationAverage: this.setEvaluationPeriodAverage,
          referenceAverage: this.setReferencesPeriodsAverage,
          referenceAverageTooltip: this.setReferencesPeriodsAverage === "Not Available" ? "There is no reference periods to calculate the average" : "",
          differenceInPerformance: this.setDryDockingDifferenceInPerformance,
        };
      case "InService Performance":
      case "Maintenance Effect":
        return {
          evaluationAverage: this.setEvaluationPeriodAverage,
          referenceAverage: this.setSelectedReferencePeriodAverage,
          differenceInPerformance: this.setDifferenceInPerformanceOfSelectedEvent,
        };
      case "Maintenance Trigger":
        return {
          evaluationAverage: this.setEvaluationPeriodAverage,
          referenceAverage: this.setSelectedReferencePeriodAverage,
          differenceInPerformance: this.setDifferenceInPerformanceOfSelectedEvent,
        };
    }
  }

  get Highchart(): any {
    return this.chart;
  }

  get vesselId(): number {
    return Number(this.$route.params.vesselId);
  }

  get referencePeriods(): PerformanceIndicatorPeriod[] {
    return this.mutatedPeriods.filter(period => period.periodType === "Reference Period");
  }

  get referencePeriodsAverage(): number {
    return this.referencePeriods.reduce((acc: number, period: PerformanceIndicatorPeriod) => acc + period.average, 0) / this.referencePeriods.length;
  }

  get selectedReferencePeriod(): PerformanceIndicatorPeriod {
    let selectedReferencePeriod!: PerformanceIndicatorPeriod;
    if (this.selectedEvent) {
      //  find period of selected event and set it as current
      const findPeriodOfSelectedEvent = this.periods.find(period => period.eventId === this.selectedEvent?.id);
      if (findPeriodOfSelectedEvent) selectedReferencePeriod = findPeriodOfSelectedEvent;
    }
    return selectedReferencePeriod;
  }

  get isEvaluationPeriodAvailable(): boolean {
    return Boolean(this.selectedEvaluationPeriod);
  }

  get selectedEvaluationPeriod(): PerformanceIndicatorPeriod {
    let selectedEvaluationPeriod!: PerformanceIndicatorPeriod;
    if (this.selectedEvent) {
      const findPeriodOfSelectedEvent = this.evaluationPeriods.find(period => period.eventId === this.selectedEvent?.id);
      if (findPeriodOfSelectedEvent) selectedEvaluationPeriod = findPeriodOfSelectedEvent;
    }

    return selectedEvaluationPeriod;
  }

  get lastDryDockingEvent(): VesselEvent | null {
    const now = moment.utc();
    if (!this.dryDockingEvents.length) return null;
    return (
      this.dryDockingEvents
        .filter(event => moment.utc(event.timestamp).isBefore(now))
        .reduce((latest, event) => (moment.utc(event.timestamp).isAfter(moment.utc(latest.timestamp)) ? event : latest)) ?? this.dryDockingEvents[0]
    );
  }

  get lastMaintenanceEvent(): VesselEvent | null {
    const now = moment.utc();
    if (!this.maintenanceEvents.length) return null;
    return (
      this.maintenanceEvents
        .filter(event => moment.utc(event.timestamp).isBefore(now))
        .reduce((latest, event) => (moment.utc(event.timestamp).isAfter(moment.utc(latest.timestamp)) ? event : latest)) ?? this.maintenanceEvents[0]
    );
  }

  get dryDockingEvents(): VesselEvent[] {
    return this.syncEvents.filter(event => event.name === "Docking (w/Hull Cleaning)");
  }

  get maintenanceEvents(): VesselEvent[] {
    return this.syncEvents.filter(event => event.name === "Hull Cleaning/Scrubbing" || event.name === "Propeller Polishing");
  }

  get chartEvents(): VesselEvent[] {
    if (this.isMaintenanceEffectChart) {
      return this.syncEvents.filter(event => event.name === "Hull Cleaning/Scrubbing" || event.name === "Propeller Polishing");
    } else {
      return this.dryDockingEvents;
    }
  }

  get evaluationPeriods(): PerformanceIndicatorPeriod[] {
    return this.mutatedPeriods.filter(period => period.periodType === "Evaluation Period");
  }

  get hasCalculatedPeriods(): boolean {
    return Boolean(this.periods.length);
  }

  get differenceInPerformanceOfSelectedEvent(): number {
    return this.selectedEvaluationPeriod?.average - this.selectedReferencePeriod?.average;
  }

  get setDifferenceInPerformanceOfSelectedEvent(): string {
    return Boolean(this.differenceInPerformanceOfSelectedEvent) ? `${this.differenceInPerformanceOfSelectedEvent?.toFixed(1)}%` : "Calculating";
  }

  get isDryDockingChart(): boolean {
    return this.title.toLowerCase().includes("docking");
  }

  get isMaintenanceEffectChart(): boolean {
    return this.title === "Maintenance Effect";
  }

  get setEvaluationPeriodAverage(): string {
    return this.isEvaluationPeriodAvailable ? `${this.selectedEvaluationPeriod.average.toFixed(1)}%` : "Not Available";
  }

  get setReferencesPeriodsAverage(): string {
    if (Boolean(this.referencePeriodsWithoutCurrentAverage)) {
      return `${this.referencePeriodsWithoutCurrentAverage.toFixed(1)}%`;
    } else {
      return "Not Available";
    }
  }

  get setSelectedReferencePeriodAverage(): string {
    return this.selectedReferencePeriod ? `${this.selectedReferencePeriod.average.toFixed(1)}%` : "Not Available";
  }

  get referencePeriodsWithoutCurrentAverage(): number {
    const referencePeriods = this.periods.filter(period => period.periodType === "Reference Period");
    const selectedReferencePeriod = this.periods.find(period => period.id === this.selectedReferencePeriod?.id);
    if (selectedReferencePeriod) {
      const referencePeriodsWithoutCurrent = referencePeriods.filter(period => {
        // check if period end date is before selected reference period start date
        if (moment(period.periodEndDate).isBefore(selectedReferencePeriod?.periodStartDate, "day") && period.id !== this.selectedReferencePeriod?.id) {
          return period;
        }
      });
      return referencePeriodsWithoutCurrent.reduce((acc: number, period: PerformanceIndicatorPeriod) => acc + period.average, 0) / referencePeriodsWithoutCurrent.length;
    } else {
      return referencePeriods.reduce((acc: number, period: PerformanceIndicatorPeriod) => acc + period.average, 0) / referencePeriods.length;
    }
  }

  get dryDockingDifferenceInPerformance(): number {
    if (!this.selectedReferencePeriod) return 0;
    const referencePeriodsWithoutCurrent = this.referencePeriods.filter(period => period.id !== this.selectedReferencePeriod?.id);
    const referencePeriodsWithoutCurrentAverage =
      referencePeriodsWithoutCurrent.reduce((acc: number, period: PerformanceIndicatorPeriod) => acc + period.average, 0) / referencePeriodsWithoutCurrent.length;
    return this.selectedReferencePeriod?.average - referencePeriodsWithoutCurrentAverage;
  }

  get setDryDockingDifferenceInPerformance(): string {
    return Boolean(this.dryDockingDifferenceInPerformance) ? `${this.dryDockingDifferenceInPerformance.toFixed(1)}%` : "Calculating";
  }

  setDryDockingEvaluationPeriod(): void {
    //  set selected reference period as evaluation period for dry docking chart
    if (!this.isDryDockingChart || !this.selectedReferencePeriod) return;
    this.mutatedPeriods = this.periods.filter((period: PerformanceIndicatorPeriod) => {
      if (moment(period.periodEndDate).isBefore(this.selectedEvent?.timestamp, "day") || period.id === this.selectedReferencePeriod.id) {
        if (this.selectedReferencePeriod.id === period.id) {
          Vue.set(period, "periodType", "Evaluation Period");
        } else {
          Vue.set(period, "periodType", "Reference Period");
        }
        return period;
      }
    });
  }

  isPeriodReferenceType(type: string): boolean {
    return type === "Reference Period";
  }

  get parsedDifferenceValue(): number {
    const raw = this.stats?.differenceInPerformance;
    if (typeof raw === "number") return raw;
    if (typeof raw === "string") {
      const trimmed = raw.trim();
      // Special textual states should not be parsed
      if (trimmed === "Calculating" || trimmed.toLowerCase().includes("not available")) return NaN;
      // Extract numeric part from strings like "1.2%" or "-0.5 %"
      const num = Number.parseFloat(trimmed.replace(/[^0-9\.-]+/g, ""));
      return Number.isFinite(num) ? num : NaN;
    }
    return NaN;
  }

  get performanceChange(): { icon: string; color: string; isCalculating: boolean } {
    const raw = this.stats?.differenceInPerformance;
    const isCalc = raw === "Calculating";
    if (isCalc) {
      return { icon: "$RefreshCircle", color: "BorderBrandSecondaryHover", isCalculating: true };
    }
    const value = this.parsedDifferenceValue;
    if (Number.isNaN(value)) {
      // fallback to positive style when unknown but not calculating
      return { icon: "$ArrowUpCircle", color: "ContentPositivePrimary", isCalculating: false };
    }
    const isNegative = value < 0;
    return {
      icon: isNegative ? "$ArrowDownCircle" : "$ArrowUpCircle",
      color: isNegative ? "ContentNegativePrimary" : "ContentPositivePrimary",
      isCalculating: false,
    };
  }

  get periodLines(): any {
    if (!this.mutatedPeriods.length) return [];
    const periodLines: any = [];
    this.mutatedPeriods.forEach((period: any) => {
      //  show period line if they have existed event
      if (this.syncEvents.filter(event => event.id === period.eventId).length) {
        periodLines.push(
          this.PeriodLine(
            Date.parse(period.periodStartDate),
            Date.parse(period.periodEndDate),
            period.average,
            this.isPeriodReferenceType(period.periodType) ? "R" : "E",
            this.isPeriodReferenceType(period.periodType) ? this.themeColor("BackgroundBrandPrimaryHover") : this.themeColor("BackgroundInfoPrimary")
          )
        );
      }
    });

    //  group lines by id to display common legend
    const firstReferenceIndex = periodLines.findIndex((line: any) => line.name === "Reference period");
    const firstEvaluationIndex = periodLines.findIndex((line: any) => line.name === "Evaluation period");
    if (firstReferenceIndex >= 0) {
      Vue.set(periodLines[firstReferenceIndex], "id", "Reference");
      Vue.delete(periodLines[firstReferenceIndex], "linkedTo");
    }
    if (firstEvaluationIndex >= 0) {
      Vue.set(periodLines[firstEvaluationIndex], "id", "Evaluation");
      Vue.delete(periodLines[firstEvaluationIndex], "linkedTo");
    }
    return periodLines;
  }

  PeriodLine(startDateMS: number, endDateMS: number, average: number, text: string, color: string): any {
    return {
      name: `${text === "R" ? "Reference" : "Evaluation"} period`,
      type: "line",
      color: color,
      lineWidth: 2,
      data: [[startDateMS, average], this.periodLineLabel(endDateMS, average, text, color)],
      linkedTo: `${text === "R" ? "Reference" : "Evaluation"}`,
      marker: {
        radius: 2.5,
        symbol: "circle",
      },
      zIndex: 5,
    };
  }

  periodLineLabel(dateMS: number, average: number, text: string, color: string): any {
    return {
      x: dateMS,
      y: average,
      dataLabels: {
        enabled: true,
        useHTML: true,
        formatter: function () {
          return `<span class="period-label" style="background-color: ${color}">${text}</span>`;
        },
        zIndex: 10,
      },
    };
  }

  get seriesData(): any {
    if (!this.performanceData.data) return [];
    if (this.chart.series.length) {
      /* IMPORTANT:
      There is also a bug in Highcharts when it has more than 2 series
      and they are updated dynamically it "loses" correct indexes of the
      series and displays data in a weird manner so the solution is to
      clear the series array before it will be updated */
      while (this.chart.series.length) {
        this.chart.series[0].remove();
      }
    }
    const scatterPoints = Object.entries(this.performanceData.data).map(([timestamp, value]) => {
      return [Date.parse(`${timestamp}Z`), value !== null ? Number(value) : null];
    });
    const scatterPointsSerie = {
      name: "Speed Loss",
      type: "scatter",
      data: scatterPoints,
      stickyTracking: false,
      /* IMPORTANT:
      https://www.highcharts.com/forum/viewtopic.php?f=9&t=44589
      Turns out scatter points have a bug. When they get updated they can be displayed as line-through-dots
      with property lineWidth: 2 so here we need to set the lineWidth: 0, otherwise it should be set always
      through chart.series.forEach loop in serie.options.lineWidth
        */
      lineWidth: 0,
      marker: {
        radius: 2.5,
        symbol: "circle",
        fillColor: this.themeColor("BorderBrandSecondaryHover"),
      },
    };
    const series = [...this.periodLines, scatterPointsSerie];

    return series;
  }

  get plotLines(): IPlotLine[] {
    if (this.isMaintenanceEffectChart) {
      return [...this.maintenancePlotLines];
    } else {
      return [...this.dockingPlotLines];
    }
  }

  get maintenancePlotLines(): IPlotLine[] {
    if (!this.syncEvents.length) return [];
    const plotLines: IPlotLine[] = [];
    this.syncEvents.forEach((event: any) => {
      if (event.name !== "Hull Cleaning/Scrubbing" && event.name !== "Propeller Polishing") return;
      const html = this.getPlotLineLabelsHtml(event);
      const value = moment.utc(event.timestamp).valueOf();
      plotLines.push({
        id: `plotline-${event.id}`,
        color: "#2196F3",
        value: value,
        width: 2,
        zIndex: 5,
        label: {
          rotation: 0,
          style: { color: "#2196F3" },
          text: html,
          useHTML: true,
          y: -15,
          x: -11,
        },
      });
    });
    return plotLines;
  }

  get dockingPlotLines(): IPlotLine[] {
    if (!this.syncEvents.length) return [];
    const plotLines: IPlotLine[] = [];

    // prepare performance points array for searching trigger crossing (sorted ascending)
    const points: [number, number | null][] = [];
    if (this.performanceData && this.performanceData.data) {
      Object.entries(this.performanceData.data).forEach(([timestamp, value]) => {
        const ms = Date.parse(`${timestamp}Z`);
        points.push([ms, value !== null ? Number(value) : null]);
      });
      points.sort((a, b) => a[0] - b[0]);
    }

    const triggerNum = Number.parseFloat(String(this.triggerValue ?? ""));
    const hasTrigger = Number.isFinite(triggerNum);

    this.syncEvents.forEach(event => {
      if (event.name !== "Docking (w/Hull Cleaning)") return;
      const value = moment.utc(event.timestamp).valueOf();

      plotLines.push(
        {
          id: `plotline-${event.id}`,
          color: this.themeColor("ContentBrandPrimarySubtle"),
          value: value,
          width: 2,
          zIndex: 7,
          label: {
            rotation: 0,
            style: { color: this.themeColor("ContentBrandPrimarySubtle") },
            text: this.getPlotLineLabelsHtml(event, "start", "d"),
            useHTML: true,
            y: -15,
            x: -11,
          },
        },
        {
          id: `plotline-${event.id}`,
          color: this.themeColor("ContentBrandPrimarySubtle"),
          value: value,
          width: 2,
          zIndex: 7,
          label: {
            rotation: 0,
            style: { color: this.themeColor("ContentBrandPrimarySubtle") },
            text: this.getPlotLineLabelsHtml(event, "end", "d"),
            useHTML: true,
            y: -15,
            x: -11,
          },
        }
      );
    });

    // Add a single trigger dotted vertical line:
    // - after the latest dry-docking event (lastDryDockingEvent)
    // - and after the evaluation period end (selectedEvaluationPeriod)
    // - find first performance point >= that cutoff and >= triggerValue
    const showTriggerLine = this.title === "Maintenance Trigger" || this.title === "InService Performance";
    if (showTriggerLine && hasTrigger && points.length) {
      const lastDocking = this.lastDryDockingEvent;
      if (lastDocking) {
        const lastDockingMs = moment.utc(lastDocking.timestamp).valueOf();
        const evalPeriodEndMs = this.selectedEvaluationPeriod && this.selectedEvaluationPeriod.periodEndDate ? Date.parse(this.selectedEvaluationPeriod.periodEndDate) : 0;
        const cutoffMs = Math.max(lastDockingMs, evalPeriodEndMs);

        const triggerPoint = points.find(p => p[0] >= cutoffMs && p[1] !== null && (p[1] as number) >= triggerNum);
        if (triggerPoint) {
          plotLines.push({
            id: `triggerline`,
            color: this.themeColor("BackgroundInfoPrimary"),
            value: triggerPoint[0],
            width: 2,
            dashStyle: "Dash",
            zIndex: 8,
            label: {
              rotation: 0,
              style: { color: this.themeColor("BackgroundInfoPrimary") },
              text: this.getTriggerPlotLineHtml(lastDocking),
              useHTML: true,
              y: -15,
              x: -11,
            },
          });
        }
      }
    }

    return plotLines;
  }

  getPlotLineLabelsHtml(event: VesselEvent, timestamp = "start", letter = "m"): string {
    return `
        <span class="plot-line-label-icon mdi mdi-alpha-${letter}-circle" style="position: relative; z-index: 10;"></span>
        <div class="plot-line-tooltip v-tooltip__content dfi-tooltip dark bottom">
          <p
          class="text-xs-medium ContentInversePrimaryStatic--text"
            style="position: absolute;
            right: 10px;
            top: 5px;"
          >ID: ${event.id}</p>
          <p style="margin-bottom: 10px;">
            <p class="text-s-medium ContentInversePrimaryStatic--text mb-1" style="text-transform: capitalize;">${timestamp} date: ${
      timestamp === "start" ? dateHelper.getFormatedDateString(event.timestamp) : dateHelper.getFormatedDateString(event.timestamp)
    }</p>
            </p>
              <p class="text-s-regular ContentInverseSecondaryStatic--text" style="display: ${!this.isMaintenanceEffectChart ? "block" : "none"}">${
      timestamp === "start" ? "In-docking" : "Out-docking"
    }</p>
      </div>
      `;
  }

  getTriggerPlotLineHtml(event: VesselEvent): string {
    return `
      <span class="plot-line-label-icon mdi mdi-flash" style="position: relative; z-index: 10; color: #fff; font-size: 12px;"></span>
      <div class="plot-line-tooltip v-tooltip__content dfi-tooltip dark bottom">
        <p class="text-s-medium ContentInversePrimaryStatic--text mb-1" style="text-transform: capitalize;">Trigger reached: ${dateHelper.getFormatedDateString(event.timestamp)}</p>
        <p class="text-s-regular ContentInverseSecondaryStatic--text">First point after docking equal or above trigger</p>
      </div>
    `;
  }

  formatDateForDisplay(date: string): string {
    return dateHelper.getFormatedDateString(date);
  }

  formatDateString(date: string | null): string {
    if (!date) return "";
    return dateHelper.getFormatedDateString(date);
  }

  get chartSettings(): any {
    if (!this.chartLoaded) return {};
    return {
      type: "line",
      height: 300,
      spacingTop: 30,
      zoomType: "x",
      backgroundColor: this.themeColor("BackgroundPrimaryDefault"),
      style: { fontFamily: "Helvetica Neue" },
    };
  }
  get ChartOptions(): any {
    const ctx = this;
    if (!this.chartLoaded || !this.seriesData.length) return { chart: ctx.chartSettings, title: { text: null }, series: [] };
    const options = {
      chart: ctx.chartSettings,
      title: { text: null },
      legend: {
        enabled: true,
        itemStyle: {
          fontSize: "14px",
          color: this.themeColor("ContentTertiary"),
          fontWeight: "400",
        },
        itemHoverStyle: {
          color: this.themeColor("ContentInverseTertiary"),
        },
      },
      navigation: {
        menuStyle: {
          color: this.themeColor("ContentPrimary"),
          backgroundColor: this.themeColor("BackgroundElevatedPrimaryDefault"),
          borderColor: this.themeColor("BorderBrandTertiary"),
          boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.08), 0px 0px 2px 0px rgba(0, 0, 0, 0.08)",
          borderRadius: "8px",
          display: "flex",
          flex: "1 0 0",
          flexDirection: "column",
          alignItems: "flex-start",
          alignSelf: "stretch",
        },
        menuItemStyle: {
          color: this.themeColor("ContentPrimary"),
          fontSize: "14px",
          fontWeight: "400",
          display: "flex",
          alignItems: "center",
          alignSelf: "stretch",
          padding: "8px",
          margin: "2px 6px",
          borderRadius: "6px",
        },
        menuItemHoverStyle: {
          background: this.themeColor("BackgroundBrandTertiaryPressed"),
          color: this.themeColor("ContentPrimary"),
        },
      },
      yAxis: {
        title: {
          text: null,
        },
        labels: {
          format: "{value}%",
          style: { color: this.themeColor("ContentPrimary") },
        },
        gridLineColor: this.themeColor("ContentInverseSecondary"),
        lineColor: this.themeColor("ContentInverseSecondary"),
        min: -50,
        max: 50,
      },
      xAxis: {
        type: "datetime",
        plotLines: this.plotLines,
        labels: {
          y: 35,
          style: { color: this.themeColor("ContentPrimary") },
        },
        minorTickLength: 0,
        tickLength: 0,
        lineColor: this.themeColor("ContentInverseSecondary"),
      },
      plotOptions: {
        scatter: {
          lineWidth: 0,
          marker: {
            radius: 2.5,
            symbol: "circle",
            fillColor: this.themeColor("BorderBrandSecondaryHover"),
          },
          tooltip: {
            outside: true,
            headerFormat: "<small>{point.key}</small><br>",
            pointFormat: "Value: <strong>{point.y}</strong>",
            valueDecimals: 1,
            valueSuffix: "%",
            xDateFormat: "%d. %b, %Y",
            zIndex: 7,
          },
        },
        line: {
          tooltip: {
            outside: true,
            headerFormat: "<small>{point.key}</small><br>",
            pointFormat: "Value: <strong>{point.y}</strong>",
            valueDecimals: 1,
            valueSuffix: "%",
            xDateFormat: "%d. %b, %Y",
            zIndex: 7,
          },
        },
        series: {
          states: {
            inactive: {
              opacity: 1,
            },
          },
        },
      },
      tooltip: {
        backgroundColor: this.themeColor("BackgroundTooltip"),
        borderWidth: 0,
        borderRadius: 16,
        padding: 12,
        format: "{value}",
        zIndex: 50,
        shadow: false,
        style: {
          color: "#EBEBEB",
        },
      },
      series: this.seriesData,
      credits: { enabled: false },
      exporting: { enabled: false },
    };

    return options;
  }

  // @Methods
  chartReady(chart: any): void {
    this.chart = chart;
    this.chart.update(this.ChartOptions, true);
    this.chartLoaded = true;
  }

  themeColor(color: string): string {
    return (this.$vuetify.theme.themes[this.$vuetify.theme.dark ? "dark" : "light"][color] as any) ?? this.$vuetify.theme.themes.light[color];
  }

  mounted(): void {
    if (this.title !== "Maintenance Effect") {
      this.selectedEvent = this.lastDryDockingEvent;
    } else {
      this.selectedEvent = this.lastMaintenanceEvent;
    }

    this.setDryDockingEvaluationPeriod();
  }
}
